import { datadogLogs } from "@datadog/browser-logs";
import { Currency } from "@redotech/money/currencies";
import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { getKey } from "@redotech/react-util/key";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { useStore } from "@redotech/react-util/store";
import { usePendingUpdates } from "@redotech/react-util/use-pending-updates";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { FulfillmentGroupStatus } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { PrintKind } from "@redotech/redo-fulfillment-group-common/fulfillment-print-status";
import { Preset } from "@redotech/redo-fulfillment-parcel-common/presets";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronUpIcon from "@redotech/redo-icons/chevron-up.svg";
import LinkIcon from "@redotech/redo-icons/link-01_filled.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { OrderDetailsInfo } from "@redotech/redo-merchant-app-common/order-details-info";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { orderProviderToLabel } from "@redotech/redo-order-core-common/order";
import { CompleteOrderFulfillmentStatus } from "@redotech/redo-shopify-order-common/shopify-order";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCommandShortcut } from "@redotech/redo-web/arbiter-components/command-menu/redo-command-shortcut";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoHorizontalTabs } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import { copyToClipboard } from "@redotech/redo-web/clipboard";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { CopyableTooltip } from "@redotech/redo-web/copyable-tooltip";
import { Flex } from "@redotech/redo-web/flex";
import { ActionPortalContext } from "@redotech/redo-web/page";
import { TabsPortal } from "@redotech/redo-web/tabs-portal";
import { RedoHorizontalTabsWithActions } from "@redotech/redo-web/tabs-with-actions";
import { Text } from "@redotech/redo-web/text";
import { sinkPromise } from "@redotech/util/promise";
import { sleep } from "@redotech/util/schedule";
import { EscapeKey } from "@redotech/web-util/key";
import {
  Dispatch,
  memo,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AddFundsModalProvider } from "./common/add-funds/add-funds-context";
import { CancelOrderModalProvider } from "./common/cancel-order/cancel-order-context";
import { CarrierAccountsProvider } from "./common/carrier-accounts-context";
import { CreateOrderSlideout } from "./common/create-order-slideout";
import { CreateReturnLabelModalProvider } from "./common/create-return-label/create-return-label-context";
import { EditAddressModalProvider } from "./common/edit-address/edit-address-context";
import { ManualOrderFormValue } from "./common/manual-order-form";
import { MarkAsFulfilledModalProvider } from "./common/mark-as-fulfilled/mark-as-fulfilled-context";
import { MissingItemsModalProvider } from "./common/missing-items/missing-items-modal-context";
import { buildNewShipmentDefaults } from "./common/new-shipment-defaults";
import { OrderNavigation } from "./common/order-navigation";
import { PackingSlipBinModalProvider } from "./common/packing-slip-bin-modal-provider";
import { PinVerificationProvider } from "./common/pin-verification-context";
import { PlaceOnHoldModalProvider } from "./common/place-on-hold/place-on-hold-context";
import {
  PrintBatchContext,
  useWorkstationOptions,
  workstationIdStore,
} from "./common/print-batch-modal";
import { SubmitShippingClaimModalProvider } from "./common/shipping-claim/shipping-claim-context";
import { FulfillmentGroupStatusBadge } from "./common/status-badge";
import { showWatchedGroupsNotification } from "./common/watched-group-notifications";
import { FulfillmentForm } from "./fulfillment-groups/fulfillment-group-form";
import { useOrderNavigation } from "./fulfillment-groups/order-navigation-context";
import * as newStyles from "./fulfillment-groups/outbound-order/new-shipment-details/outbound-order.module.css";
import { OutboundOrderFulfillmentGroup as NewOutboundOrderFulfillmentGroup } from "./fulfillment-groups/outbound-order/new-shipment-details/shipment-tab";
import * as originalStyles from "./fulfillment-groups/outbound-order/outbound-order.module.css";
import { OutboundOrderFulfillmentGroup } from "./fulfillment-groups/outbound-order/shipment-tab";
import {
  OrderItems,
  OrderSummary,
  PaymentSummary,
} from "./fulfillment-groups/outbound-order/shipment-tab-order-details";
import { usePollUpdates } from "./hooks";
import { RedoOutboundLabelsRpcClientContext } from "./outbound-labels-rpc-client";
import { isNewShipmentDetailsEnabled } from "./outbound-order-routing";
import {
  appendNewShipmentsForVoidedGroups,
  applyWatchedFulfillmentGroupUpdates,
} from "./outbound-order-shipment-updates";
import { LocationContext } from "./services/location-service";
import { ParcelContext } from "./services/parcel-service";
import { getOptimisticUpdates } from "./utils/get-optimistic-updates";
import { getTeamOMSUsers } from "./utils/get-team-oms-users";
import { updateFulfillmentGroupsWithAlertFailure } from "./utils/update-fulfillment-groups-with-retry";
import { useFulfillmentGroupPermissions } from "./utils/use-assigned-fulfillment-service";

export const TAB_PARAM = "outboundOrderTab";
export const ORDER_OVERVIEW = "orderOverview";
const SHIPMENT_WORKSPACE_SOURCE_VIEW = "shipments";
const SHIPMENT_NAVIGATION_LIST_ID = "shipment-navigation-list";

const decodeOrderId = (encodedOrderId: string): string => {
  try {
    return atob(encodedOrderId);
  } catch {
    return encodedOrderId;
  }
};

const getTabLabel = (tab: string, index: number) => {
  if (tab === ORDER_OVERVIEW) {
    return "Order overview";
  } else {
    return `Shipment ${index}`;
  }
};

function pluralize(count: number, singular: string): string {
  return `${count} ${singular}${count === 1 ? "" : "s"}`;
}

function getShipmentItemCount(fulfillmentGroup: FulfillmentGroupData): number {
  return fulfillmentGroup.items.reduce(
    (total, item) => total + (item.quantity ?? item.totalQuantity ?? 0),
    0,
  );
}

function getShipmentPackageCount(
  fulfillmentGroup: FulfillmentGroupData,
): number {
  return fulfillmentGroup.selectedParcels?.length || 1;
}

function getOrderItemCount(orderInfo: OrderDetailsInfo): number {
  return orderInfo.lineItems.reduce(
    (total, lineItem) => total + (lineItem.quantity ?? 0),
    0,
  );
}

function getOrderSource(orderInfo: OrderDetailsInfo): string {
  if (orderInfo.provider) {
    return orderProviderToLabel[orderInfo.provider];
  }
  return orderInfo.order?.shopify?.source_name || "Unknown source";
}

function getOrderFulfillmentStatus(
  orderInfo: OrderDetailsInfo,
): string | undefined {
  return CompleteOrderFulfillmentStatus.fromDbValue(orderInfo.fulfillmentStatus)
    ?.displayValue;
}

function formatOrderTotal(orderInfo: OrderDetailsInfo): string | undefined {
  if (
    orderInfo.totalPrice == null ||
    orderInfo.totalPrice === "" ||
    isNaN(Number(orderInfo.totalPrice))
  ) {
    return undefined;
  }

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: orderInfo.currency ?? Currency.USD,
  }).format(Number(orderInfo.totalPrice));
}

function getShipmentLabelStatus(
  fulfillmentGroup: FulfillmentGroupData,
): string {
  if (fulfillmentGroup.buyLabelErrors?.length) {
    return "Label failed";
  }

  if (!fulfillmentGroup.shipment) {
    return "No label";
  }

  return fulfillmentGroup.printStatus?.[PrintKind.LABEL]
    ? "Label printed"
    : "Label not printed";
}

export const OutboundOrder = memo(function OutboundOrder({
  children,
  orderInfo,
}: {
  children: (orderInfo: OrderDetailsInfo) => ReactNode | ReactNode[];
  orderInfo: OrderDetailsInfo | null | undefined;
}) {
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const team = useContext(TeamContext);
  const batchPrinting = useContext(PrintBatchContext);
  const params = useParams<{ orderId: string }>();
  const { orderId: encodedOrderId } = params;

  const [searchParams, setSearchParams] = useSearchParams();
  const newShipmentDetailsEnabled = isNewShipmentDetailsEnabled({
    featureFlags: team?.featureFlags,
  });
  const isRedoOrder = searchParams.get("isRedoOrder");
  const orderId =
    encodedOrderId && isRedoOrder === "false"
      ? decodeOrderId(encodedOrderId)
      : encodedOrderId;

  const urlTab = searchParams.get(TAB_PARAM);
  const [activeTab, setActiveTab] = useState<string>(urlTab || ORDER_OVERVIEW);
  useEffect(() => {
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab]);
  const activeTabRef = useRef(activeTab);
  const searchParamsRef = useRef(searchParams);
  activeTabRef.current = activeTab;
  searchParamsRef.current = searchParams;
  const useNewShipmentDetails = newShipmentDetailsEnabled;
  const [refreshSymbol, setRefreshSymbol] = useState<symbol>(Symbol());
  const [selectedWorkstationId, setSelectedWorkstationId] =
    useStore(workstationIdStore);
  const workstationOptions = useWorkstationOptions(
    team?.settings?.outboundLabels?.workstations,
  );
  const selectedWorkstationOption = useMemo(
    () =>
      workstationOptions.find(
        (workstation) => workstation.value === selectedWorkstationId,
      ) ?? workstationOptions.find((workstation) => workstation.value === ""),
    [selectedWorkstationId, workstationOptions],
  );
  const navigate = useNavigate();
  const { fulfillmentGroupInfo, orderPagePrefetchedInfo, returnUrl } =
    useOrderNavigation();

  const onClose = useHandler(() => {
    if (returnUrl) {
      navigate(returnUrl);
    } else {
      navigate(useNewShipmentDetails ? ".." : "../../awaiting-shipment");
    }
  });

  useEffect(() => {
    if (searchParams.get(TAB_PARAM) !== activeTab) {
      setActiveTab(searchParams.get(TAB_PARAM) || ORDER_OVERVIEW);
    }
  }, [activeTab, setSearchParams, searchParams]);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, [onClose]);

  const fulfillmentGroupsLoad = useLoad(
    async (signal) => {
      if (!team || !orderId) {
        return { orderId: undefined, groups: [] };
      }

      if (orderPagePrefetchedInfo[orderId]) {
        return {
          orderId,
          groups: orderPagePrefetchedInfo[orderId].fulfillmentGroupInfo,
        };
      }

      let params: { orderId: string } | { orderExternalId: string };
      if (isRedoOrder !== "false") {
        // Default to redo order unless explicitly false
        params = { orderId };
      } else {
        params = { orderExternalId: orderId };
      }
      const { fulfillmentGroups } =
        await rpcClient.getFulfillmentGroupsForOrder(params, { signal });
      return { orderId, groups: fulfillmentGroups };
    },
    [orderId, orderInfo, refreshSymbol],
  );

  const [fulfillmentGroupData, setFulfillmentGroupData] = useState<
    FulfillmentGroupData[]
  >([]);
  const [loadedGroupsOrderId, setLoadedGroupsOrderId] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (fulfillmentGroupsLoad.value) {
      setFulfillmentGroupData(fulfillmentGroupsLoad.value.groups);
      setLoadedGroupsOrderId(fulfillmentGroupsLoad.value.orderId);
    }
  }, [fulfillmentGroupsLoad.value]);

  useEffect(() => {
    if (
      loadedGroupsOrderId === orderId &&
      activeTab !== ORDER_OVERVIEW &&
      fulfillmentGroupData.length > 0 &&
      !fulfillmentGroupData.some((group) => group._id === activeTab)
    ) {
      const firstGroup = fulfillmentGroupData[0];
      if (!firstGroup) {
        return;
      }
      setActiveTab(firstGroup._id);
      const newParams = new URLSearchParams(searchParams);
      newParams.set(TAB_PARAM, firstGroup._id);
      setSearchParams(newParams, { replace: true });
    }
  }, [
    activeTab,
    fulfillmentGroupData,
    loadedGroupsOrderId,
    orderId,
    searchParams,
    setSearchParams,
  ]);

  const updateGroups = useHandler(async (updates: FulfillmentGroupUpdate[]) => {
    const updatedGroups = await updateFulfillmentGroupsWithAlertFailure(
      rpcClient,
      updates,
      { teamId: team?._id },
    );
    return Object.values(updatedGroups);
  });

  const { updatesPending, hasPendingUpdate, addPendingUpdates } =
    usePendingUpdates({
      updateGroups,
      onUpdatesComplete: (groups) => pushUpdatedGroups(groups),
      identifier: (update) => update.fulfillmentGroupId,
    });

  const handleUserUpdate = useHandler(
    (fulfillmentGroupUpdates: FulfillmentGroupUpdate[]) => {
      const { newData, updates } = getOptimisticUpdates(
        fulfillmentGroupData,
        fulfillmentGroupUpdates,
      );
      setFulfillmentGroupData(newData);
      addPendingUpdates(updates);
    },
  );

  const onGroupsFulfillment = useHandler((groupIds: string[]) => {
    // Optimistically update groups to PENDING status for this order page
    const newData = fulfillmentGroupData.map((group) => {
      if (groupIds.includes(group._id)) {
        return { ...group, status: FulfillmentGroupStatus.Pending };
      }
      return group;
    });
    setFulfillmentGroupData(newData);
  });

  const groupsWaitingForNewShipment = useRef<string[]>([]);
  const voidedGroupNewShipmentIds = useRef<Record<string, string>>({});

  const appendNewShipmentsForVoidedGroupIds = useHandler(
    async (groupIds: string[]) => {
      if (!groupIds.length) {
        return;
      }

      const { replacements: newShipmentResponses } =
        await rpcClient.getReplacementGroups({ groupIds });

      const groupIdsWithNewShipments = newShipmentResponses.map(
        ({ groupId }) => groupId,
      );
      if (!groupIdsWithNewShipments.length) {
        return;
      }

      groupsWaitingForNewShipment.current =
        groupsWaitingForNewShipment.current.filter(
          (groupId) => !groupIdsWithNewShipments.includes(groupId),
        );
      voidedGroupNewShipmentIds.current = {
        ...voidedGroupNewShipmentIds.current,
        ...Object.fromEntries(
          newShipmentResponses.map(({ groupId, replacementGroup }) => [
            groupId,
            replacementGroup._id,
          ]),
        ),
      };

      setFulfillmentGroupData((prev) =>
        appendNewShipmentsForVoidedGroups(prev, newShipmentResponses),
      );

      const currentActiveTab =
        searchParamsRef.current.get(TAB_PARAM) || activeTabRef.current;
      const activeTabNewShipment = newShipmentResponses.find(
        ({ groupId }) => groupId === currentActiveTab,
      );
      if (activeTabNewShipment) {
        const newShipmentGroupId = activeTabNewShipment.replacementGroup._id;
        setActiveTab(newShipmentGroupId);

        const newParams = new URLSearchParams(searchParamsRef.current);
        newParams.set(TAB_PARAM, newShipmentGroupId);
        setSearchParams(newParams, { replace: true });
      }
    },
  );

  const loadNewShipmentsForVoidedGroups = useHandler(
    async (groups: FulfillmentGroupData[]) => {
      const groupIdsNeedingNewShipment = groups
        .filter((group) =>
          groupsWaitingForNewShipment.current.includes(group._id),
        )
        .filter((group) => group.pointerToNextGroup)
        .map((group) => group._id);

      await appendNewShipmentsForVoidedGroupIds(groupIdsNeedingNewShipment);
    },
  );

  const pollForNewShipmentsAfterVoid = useHandler(
    async (groupIds: string[]) => {
      for (let attempt = 0; attempt < 10; attempt++) {
        const pendingGroupIds = groupIds.filter((groupId) =>
          groupsWaitingForNewShipment.current.includes(groupId),
        );
        if (!pendingGroupIds.length) {
          return;
        }

        await sleep(Temporal.Duration.from({ seconds: 2 }));
        await appendNewShipmentsForVoidedGroupIds(pendingGroupIds);
      }
    },
  );

  const onGroupsVoided = useHandler((groupIds: string[]) => {
    groupsWaitingForNewShipment.current = Array.from(
      new Set([...groupsWaitingForNewShipment.current, ...groupIds]),
    );
    void pollForNewShipmentsAfterVoid(groupIds).catch((error: unknown) => {
      datadogLogs.logger.warn("Failed to poll for new shipment after void", {
        error,
        groupIds,
      });
    });
  });

  const pushUpdatedGroups = useHandler((groups: FulfillmentGroupData[]) => {
    void loadNewShipmentsForVoidedGroups(groups).catch((error: unknown) => {
      datadogLogs.logger.warn("Failed to append new shipments after void", {
        error,
        groupIds: groups.map((group) => group._id),
      });
    });

    setFulfillmentGroupData((prev) => {
      const newGroups = applyWatchedFulfillmentGroupUpdates({
        groups: prev,
        updates: groups,
        hasPendingUpdate,
        groupsWaitingForNewShipment: groupsWaitingForNewShipment.current,
        voidedGroupNewShipmentIds: voidedGroupNewShipmentIds.current,
      });

      showWatchedGroupsNotification({
        rows: prev,
        newRows: newGroups,
        printBatch: batchPrinting.printBatch,
      });

      return newGroups;
    });
  });

  useLoad(
    async (signal) => {
      if (!fulfillmentGroupData?.length) {
        return;
      }
      let errorCount = 0;
      while (true) {
        try {
          // Backoff if we're getting back to back errors
          await sleep(Temporal.Duration.from({ seconds: errorCount }), signal);
          const uniqueGroupIds = new Set(
            fulfillmentGroupData.map(({ _id }) => _id),
          );

          let logged = false;
          const start = performance.now();
          const iterator = rpcClient.watchFulfillmentGroups(
            {
              fulfillmentGroupIds: Array.from(uniqueGroupIds),
              maxUpdatedAt: fulfillmentGroupData.reduce((max, group) => {
                const updatedAt = group.updatedAt ?? new Date();
                return updatedAt > max ? updatedAt : max;
              }, new Date()),
            },
            { signal },
          );
          for await (const { fulfillmentGroups } of iterator) {
            if (!logged) {
              logged = true;
              datadogLogs.logger.info("initial watchFulfillmentGroups", {
                duration: performance.now() - start,
              });
            }
            pushUpdatedGroups(fulfillmentGroups);
            errorCount = 0;
          }
        } catch (e) {
          if (signal.aborted) {
            break;
          }
          errorCount++;
        }
      }
    },
    [fulfillmentGroupData.map(({ _id }) => _id).join(",")],
  );

  usePollUpdates({
    groups: fulfillmentGroupData,
    onGroupsUpdated: pushUpdatedGroups,
  });

  const tabs: string[] = useMemo(() => {
    const tabs = [ORDER_OVERVIEW];
    for (const fulfillmentGroupDatum of fulfillmentGroupData) {
      const groupId = fulfillmentGroupDatum._id;
      if (groupId) {
        tabs.push(groupId);
      }
    }
    return tabs;
  }, [fulfillmentGroupData]);

  const presets = useLoad(async () => {
    return (await rpcClient.getPresets({})).presets;
  }, []);

  const [createShipmentOpen, setCreateShipmentOpen] = useState(false);
  const [workspaceActionsElement, setWorkspaceActionsElement] =
    useState<HTMLElement | null>(null);
  const [locationsLoad] = useLazyContext(LocationContext);

  const selectedSourceGroup = useMemo(() => {
    return (
      fulfillmentGroupData.find((group) => group._id === activeTab) ??
      fulfillmentGroupData[0]
    );
  }, [fulfillmentGroupData, activeTab]);

  const currentSourceFulfillmentGroupId = useMemo(() => {
    return (
      fulfillmentGroupInfo.find((group) => group.id === urlTab)?.id ??
      fulfillmentGroupInfo.find((group) => group.orderId === orderId)?.id
    );
  }, [fulfillmentGroupInfo, orderId, urlTab]);

  const newShipmentDefaults = useMemo(
    (): Partial<ManualOrderFormValue> =>
      selectedSourceGroup
        ? buildNewShipmentDefaults(selectedSourceGroup, locationsLoad.value)
        : {},
    [selectedSourceGroup, locationsLoad.value],
  );

  const addNewShipment = useHandler((groups: FulfillmentGroupData[]) => {
    setFulfillmentGroupData((prev) => [...prev, ...groups]);
    const newGroup = groups[0];
    if (newGroup) {
      setActiveTab(newGroup._id);
      const newParams = new URLSearchParams(searchParams);
      newParams.set(TAB_PARAM, newGroup._id);
      setSearchParams(newParams, { replace: true });
    }
  });

  const copyOrderLink = useHandler((event: ReactMouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    sinkPromise(copyToClipboard(window.location.href));
  });

  const selectTab = useHandler((tab: string) => {
    setActiveTab(tab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set(TAB_PARAM, tab);
    if (
      tab === ORDER_OVERVIEW &&
      activeTab !== ORDER_OVERVIEW &&
      !newParams.has("sourceView")
    ) {
      newParams.set("sourceView", SHIPMENT_WORKSPACE_SOURCE_VIEW);
    }
    setSearchParams(newParams, { replace: true });
  });

  const isShipmentWorkspaceMobile = useScreenType(1024) === ScreenType.MOBILE;

  if (!orderInfo) {
    return null;
  }

  const fulfillmentGroup = fulfillmentGroupData.find(
    (group) => group._id === activeTab,
  );
  const index = fulfillmentGroup
    ? fulfillmentGroupData.indexOf(fulfillmentGroup)
    : -1;
  const ratesPending = !!fulfillmentGroup?.ratesPendingSince;

  return (
    <CarrierAccountsProvider>
      <MarkAsFulfilledModalProvider>
        <CancelOrderModalProvider>
          <PlaceOnHoldModalProvider>
            <SubmitShippingClaimModalProvider>
              <AddFundsModalProvider>
                <CreateReturnLabelModalProvider>
                  <MissingItemsModalProvider>
                    {useNewShipmentDetails ? (
                      <ActionPortalContext.Provider
                        value={workspaceActionsElement || undefined}
                      >
                        <Flex
                          className={newStyles.shipmentWorkspace}
                          dir="column"
                          gap="none"
                          height={isShipmentWorkspaceMobile ? "auto" : "full"}
                          minH={isShipmentWorkspaceMobile ? "full" : "none"}
                          overflow={
                            isShipmentWorkspaceMobile ? "visible" : "hidden"
                          }
                          w="full"
                        >
                          <CreateOrderSlideout
                            addGroupsToTable={addNewShipment}
                            defaultValues={newShipmentDefaults}
                            key={getKey(newShipmentDefaults)}
                            onClose={() => setCreateShipmentOpen(false)}
                            open={createShipmentOpen}
                            orderId={selectedSourceGroup?.orders[0]?.id}
                            redoOrderId={
                              selectedSourceGroup?.orders[0]?.redoOrderId
                            }
                          />
                          <Flex
                            align="center"
                            className={newStyles.shipmentWorkspaceHeader}
                            justify="space-between"
                            w="full"
                          >
                            <Flex align="center" gap="sm">
                              <CopyableTooltip textToCopy="Click to copy page link">
                                <Text
                                  className={newStyles.orderLinkTrigger}
                                  fontSize="lg"
                                  fontWeight="medium"
                                  onClick={copyOrderLink}
                                >
                                  {orderInfo.orderName}
                                </Text>
                              </CopyableTooltip>
                              <CopyableTooltip textToCopy="Click to copy page link">
                                <RedoButton
                                  aria-label="Copy link"
                                  hierarchy="tertiary"
                                  IconLeading={LinkIcon}
                                  onClick={copyOrderLink}
                                />
                              </CopyableTooltip>
                            </Flex>
                            <Flex align="center" gap="lg" justify="flex-end">
                              {workstationOptions.length > 0 && (
                                <RedoSingleSelectDropdownInput
                                  fitToAnchor={false}
                                  options={workstationOptions}
                                  optionSelected={({ item }) =>
                                    setSelectedWorkstationId(item.value)
                                  }
                                  placeholder="Select workstation"
                                  selectedItem={selectedWorkstationOption}
                                  size="xs"
                                />
                              )}
                              <Flex
                                align="center"
                                gap="lg"
                                ref={setWorkspaceActionsElement}
                              />
                              {activeTab !== ORDER_OVERVIEW && (
                                <RedoButton
                                  dontRestrainIconWidth
                                  hierarchy="secondary"
                                  IconTrailing={() => (
                                    <RedoCommandShortcut hotkey={[EscapeKey]} />
                                  )}
                                  onClick={onClose}
                                  size="sm"
                                  text="Close"
                                />
                              )}
                              {activeTab !== ORDER_OVERVIEW &&
                                currentSourceFulfillmentGroupId && (
                                  <OrderNavigation
                                    currentFulfillmentGroupId={
                                      currentSourceFulfillmentGroupId
                                    }
                                    pb="none"
                                  />
                                )}
                            </Flex>
                          </Flex>
                          <Flex
                            dir={isShipmentWorkspaceMobile ? "column" : "row"}
                            flex={
                              isShipmentWorkspaceMobile ? "none" : "1 1 auto"
                            }
                            gap="none"
                            minH={isShipmentWorkspaceMobile ? "auto" : "none"}
                            overflow={
                              isShipmentWorkspaceMobile ? "visible" : "hidden"
                            }
                            w="full"
                          >
                            <ShipmentNavigationRail
                              activeTab={activeTab}
                              fulfillmentGroups={fulfillmentGroupData}
                              orderInfo={orderInfo}
                              selectTab={selectTab}
                            />
                            <Flex
                              className={newStyles.shipmentWorkspaceContent}
                              flex={
                                isShipmentWorkspaceMobile ? "none" : "1 1 auto"
                              }
                              gap="none"
                              minH={isShipmentWorkspaceMobile ? "auto" : "none"}
                              minW="none"
                              overflow={
                                isShipmentWorkspaceMobile
                                  ? "visible"
                                  : activeTab === ORDER_OVERVIEW
                                    ? "auto"
                                    : "hidden"
                              }
                            >
                              {activeTab === ORDER_OVERVIEW
                                ? children(orderInfo) // TODO swap with improved order component
                                : fulfillmentGroup &&
                                  index >= 0 && (
                                    <GroupDetails
                                      fulfillmentGroup={fulfillmentGroup}
                                      handleUpdates={handleUserUpdate}
                                      index={index}
                                      numGroups={fulfillmentGroupData.length}
                                      onGroupsFulfillment={onGroupsFulfillment}
                                      onGroupsVoided={onGroupsVoided}
                                      orderInfo={orderInfo}
                                      presets={presets?.value || []}
                                      setFulfillmentGroupData={
                                        setFulfillmentGroupData
                                      }
                                      setRefreshSymbol={setRefreshSymbol}
                                      updatesPending={
                                        updatesPending || ratesPending
                                      }
                                    />
                                  )}
                            </Flex>
                          </Flex>
                        </Flex>
                      </ActionPortalContext.Provider>
                    ) : (
                      <Flex w="full">
                        <TabsPortal>
                          <RedoHorizontalTabsWithActions
                            actions={
                              <Flex align="center" gap="md">
                                <RedoButton
                                  hierarchy="tertiary"
                                  IconLeading={PlusIcon}
                                  onClick={() => setCreateShipmentOpen(true)}
                                  size="xs"
                                  text="New shipment"
                                />
                                <OrderNavigation
                                  currentFulfillmentGroupId={
                                    currentSourceFulfillmentGroupId ?? activeTab
                                  }
                                />
                              </Flex>
                            }
                            tabs={
                              <RedoHorizontalTabs
                                drawBottomLine={false}
                                pl="none"
                                selectedTab={activeTab}
                                setSelectedTab={selectTab}
                                tabs={tabs.map((tab, index) => ({
                                  label: getTabLabel(tab, index),
                                  value: tab,
                                  key: tab,
                                }))}
                              />
                            }
                          />
                        </TabsPortal>

                        <CreateOrderSlideout
                          addGroupsToTable={addNewShipment}
                          defaultValues={newShipmentDefaults}
                          key={getKey(newShipmentDefaults)}
                          onClose={() => setCreateShipmentOpen(false)}
                          open={createShipmentOpen}
                          orderId={selectedSourceGroup?.orders[0]?.id}
                          redoOrderId={
                            selectedSourceGroup?.orders[0]?.redoOrderId
                          }
                        />
                        <>
                          {activeTab === ORDER_OVERVIEW
                            ? children(orderInfo) // TODO swap with improved order component
                            : fulfillmentGroup &&
                              index >= 0 && (
                                <OriginalGroupDetails
                                  fulfillmentGroup={fulfillmentGroup}
                                  handleUpdates={handleUserUpdate}
                                  index={index}
                                  numGroups={fulfillmentGroupData.length}
                                  onGroupsFulfillment={onGroupsFulfillment}
                                  order={orderInfo}
                                  presets={presets?.value || []}
                                  setRefreshSymbol={setRefreshSymbol}
                                  updatesPending={
                                    updatesPending || ratesPending
                                  }
                                />
                              )}
                        </>
                      </Flex>
                    )}
                  </MissingItemsModalProvider>
                </CreateReturnLabelModalProvider>
              </AddFundsModalProvider>
            </SubmitShippingClaimModalProvider>
          </PlaceOnHoldModalProvider>
        </CancelOrderModalProvider>
      </MarkAsFulfilledModalProvider>
    </CarrierAccountsProvider>
  );
});

const ShipmentNavigationRail = memo(function ShipmentNavigationRail({
  activeTab,
  fulfillmentGroups,
  orderInfo,
  selectTab,
}: {
  activeTab: string;
  fulfillmentGroups: FulfillmentGroupData[];
  orderInfo: OrderDetailsInfo;
  selectTab: (tab: string) => void;
}) {
  const [shipmentsListExpanded, setShipmentsListExpanded] = useState(false);
  const isMobile = useScreenType(1024) === ScreenType.MOBILE;

  return (
    <Flex
      className={newStyles.shipmentNavigationRail}
      dir="column"
      flex={isMobile ? "none" : "0 0 240px"}
      gap="none"
      minH={isMobile ? "auto" : "none"}
      overflowY={isMobile ? "visible" : "auto"}
      w={isMobile ? "full" : undefined}
    >
      <Flex
        align="center"
        className={newStyles.shipmentNavigationRailHeader}
        justify="space-between"
      >
        <Text fontSize="sm" fontWeight="medium">
          Shipments
        </Text>
        {isMobile && (
          <RedoButton
            aria-controls={SHIPMENT_NAVIGATION_LIST_ID}
            aria-expanded={shipmentsListExpanded}
            aria-label={
              shipmentsListExpanded ? "Collapse shipments" : "Expand shipments"
            }
            hierarchy="tertiary"
            IconLeading={
              shipmentsListExpanded ? ChevronUpIcon : ChevronDownIcon
            }
            onClick={() => setShipmentsListExpanded((expanded) => !expanded)}
            size="xs"
          />
        )}
      </Flex>
      <Flex
        dir="column"
        gap="none"
        hidden={isMobile && !shipmentsListExpanded}
        id={SHIPMENT_NAVIGATION_LIST_ID}
        overflowY="auto"
      >
        <ShipmentNavigationCard
          fulfillmentGroups={fulfillmentGroups}
          isActive={activeTab === ORDER_OVERVIEW}
          onClick={() => selectTab(ORDER_OVERVIEW)}
          orderInfo={orderInfo}
          title={getTabLabel(ORDER_OVERVIEW, 0)}
        />
        {fulfillmentGroups.map((group, index) => (
          <ShipmentNavigationCard
            fulfillmentGroup={group}
            isActive={activeTab === group._id}
            key={group._id}
            onClick={() => selectTab(group._id)}
            title={getTabLabel(group._id, index + 1)}
          />
        ))}
      </Flex>
    </Flex>
  );
});

const ShipmentNavigationCard = memo(function ShipmentNavigationCard({
  fulfillmentGroup,
  fulfillmentGroups,
  isActive,
  onClick,
  orderInfo,
  title,
}: {
  fulfillmentGroup?: FulfillmentGroupData;
  fulfillmentGroups?: FulfillmentGroupData[];
  isActive: boolean;
  onClick: () => void;
  orderInfo?: OrderDetailsInfo;
  title: string;
}) {
  const itemCount = fulfillmentGroup
    ? getShipmentItemCount(fulfillmentGroup)
    : null;
  const packageCount = fulfillmentGroup
    ? getShipmentPackageCount(fulfillmentGroup)
    : null;
  const overviewItemCount = fulfillmentGroups?.length
    ? fulfillmentGroups.reduce(
        (total, group) => total + getShipmentItemCount(group),
        0,
      )
    : orderInfo
      ? getOrderItemCount(orderInfo)
      : 0;
  const overviewPackageCount = fulfillmentGroups?.reduce(
    (total, group) => total + getShipmentPackageCount(group),
    0,
  );
  const orderTotal = orderInfo ? formatOrderTotal(orderInfo) : undefined;
  const orderFulfillmentStatus = orderInfo
    ? getOrderFulfillmentStatus(orderInfo)
    : undefined;
  const onCardKeyDown = useHandler(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    },
  );

  return (
    <Flex
      aria-current={isActive ? "page" : undefined}
      bgColor={isActive ? "quaternary" : "primary"}
      borderBottomWidth="1px"
      borderColor="secondary"
      borderStyle="solid"
      className={newStyles.shipmentNavigationCard}
      cursor="pointer"
      hoverColor={isActive ? undefined : "primary"}
      onClick={onClick}
      onKeyDown={onCardKeyDown}
      p="lg"
      role="button"
      tabIndex={0}
    >
      <Flex dir="column" gap="sm" overflow="hidden" w="full">
        <Flex align="center" gap="sm" justify="space-between">
          <Text fontSize="xs" fontWeight="medium">
            {title}
          </Text>
          {fulfillmentGroup && (
            <FulfillmentGroupStatusBadge
              fulfillmentGroupStatus={fulfillmentGroup.status}
              size="xs"
            />
          )}
          {!fulfillmentGroup && orderInfo && (
            <RedoBadge size="xs" text={getOrderSource(orderInfo)} />
          )}
        </Flex>
        {fulfillmentGroup && itemCount != null && packageCount != null && (
          <Flex dir="column" gap="none">
            <Text fontSize="xs" textColor="tertiary">
              {pluralize(itemCount, "item")} ·{" "}
              {pluralize(packageCount, "package")}
            </Text>
            <Text fontSize="xs" textColor="tertiary">
              {getShipmentLabelStatus(fulfillmentGroup)}
            </Text>
          </Flex>
        )}
        {!fulfillmentGroup && orderInfo && (
          <Flex dir="column" gap="none">
            <Text fontSize="xs" textColor="tertiary">
              {pluralize(overviewItemCount, "item")}
            </Text>
            <Text fontSize="xs" textColor="tertiary">
              {pluralize(fulfillmentGroups?.length ?? 0, "shipment")} ·{" "}
              {pluralize(overviewPackageCount ?? 0, "package")}
            </Text>
            {(orderTotal || orderFulfillmentStatus) && (
              <Text fontSize="xs" textColor="tertiary">
                {[orderTotal, orderFulfillmentStatus]
                  .filter(Boolean)
                  .join(" · ")}
              </Text>
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

const OriginalGroupDetails = memo(function OriginalGroupDetails({
  fulfillmentGroup,
  index,
  numGroups,
  presets,
  order,
  setRefreshSymbol,
  handleUpdates,
  updatesPending,
  onGroupsFulfillment,
}: {
  fulfillmentGroup: FulfillmentGroupData;
  index: number;
  numGroups: number;
  presets: Preset[];
  order: OrderDetailsInfo;
  setRefreshSymbol: (symbol: symbol) => void;
  handleUpdates: (updates: FulfillmentGroupUpdate[]) => void;
  updatesPending: boolean;
  onGroupsFulfillment: (groupIds: string[]) => void;
}) {
  const navigate = useNavigate();
  const { returnUrl } = useOrderNavigation();

  const onClose = useHandler(() => {
    if (returnUrl) {
      navigate(returnUrl);
    } else {
      navigate("..");
    }
  });

  const actionsPortal = useContext(ActionPortalContext);

  const closeButton = (
    <RedoButton
      dontRestrainIconWidth
      hierarchy="secondary"
      IconTrailing={() => <RedoCommandShortcut hotkey={[EscapeKey]} />}
      onClick={onClose}
      size="sm"
      text="Close"
    />
  );

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, [onClose]);

  return (
    <>
      {actionsPortal && createPortal(closeButton, actionsPortal)}
      <Flex
        className={originalStyles.orderDetailsWrapper}
        justify="space-between"
      >
        <Flex w="full">
          <OutboundOrderFulfillmentGroup
            fulfillmentGroup={fulfillmentGroup}
            handleUpdates={handleUpdates}
            index={index}
            onGroupsFulfillment={onGroupsFulfillment}
            presets={presets}
            setRefreshSymbol={setRefreshSymbol}
            totalGroups={numGroups}
            updatesPending={updatesPending}
          />
        </Flex>
        <Flex
          borderColor="secondary"
          borderLeftWidth="1px"
          borderStyle="solid"
          className={originalStyles.orderInfoContainer}
          flexDirection="column"
          gap="none"
          height="fit-content"
        >
          <OrderSummary orderInfo={order} />
          <PaymentSummary
            billingAddress={order.billingAddress}
            createdAt={order.createdAt}
            currency={order.currency}
            landedCost={order.landedCost}
            totalDiscounts={order.totalDiscounts}
            totalLineItemsPrice={order.totalLineItemsPrice}
            totalPrice={order.totalPrice}
            totalShippingPrice={order.totalShippingPrice}
            totalTax={order.totalTax}
            transactions={order.transactions ?? []}
          />
          <OrderItems currency={order.currency} lineItems={order.lineItems} />
        </Flex>
      </Flex>
    </>
  );
});

const GroupDetails = memo(function GroupDetails({
  fulfillmentGroup,
  index,
  numGroups,
  presets,
  setRefreshSymbol,
  handleUpdates,
  updatesPending,
  onGroupsFulfillment,
  onGroupsVoided,
  setFulfillmentGroupData,
  orderInfo,
}: {
  fulfillmentGroup: FulfillmentGroupData;
  index: number;
  numGroups: number;
  presets: Preset[];
  setRefreshSymbol: (symbol: symbol) => void;
  handleUpdates: (updates: FulfillmentGroupUpdate[]) => void;
  updatesPending: boolean;
  onGroupsFulfillment: (groupIds: string[]) => void;
  onGroupsVoided: (groupIds: string[]) => void;
  setFulfillmentGroupData: Dispatch<SetStateAction<FulfillmentGroupData[]>>;
  orderInfo: OrderDetailsInfo;
}) {
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const team = useContext(TeamContext);
  const [parcelsLoad] = useLazyContext(ParcelContext);

  const { permissionsToEditEveryGroup } = useFulfillmentGroupPermissions([
    fulfillmentGroup,
  ]);
  const teamUsers = team ? getTeamOMSUsers(team) : [];

  const [balanceLoad, reloadBalance] = useTriggerLoad(
    async (signal: AbortSignal) => {
      try {
        return await rpcClient.getOutboundBalance(null, { signal });
      } catch {
        return { balance: "0" };
      }
    },
  );

  useEffect(() => {
    reloadBalance();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerRefresh = useHandler(() => {
    setRefreshSymbol(Symbol());
  });

  const handleChangeLocations = useHandler(() => {
    triggerRefresh();
  });

  const handleSetSelectedFulfillmentGroups = useHandler(
    (groups: FulfillmentGroupData[]) => {
      setFulfillmentGroupData((prev) => [...prev, ...groups]);
    },
  );
  const isMobile = useScreenType(1024) === ScreenType.MOBILE;

  return (
    <Flex
      className={newStyles.orderDetailsWrapper}
      dir={isMobile ? "column" : "row"}
      gap="none"
      height={isMobile ? "auto" : "full"}
      maxHeight={isMobile ? "none" : "full"}
      minH={isMobile ? "auto" : "none"}
      overflow={isMobile ? "visible" : "hidden"}
      w="full"
    >
      <Flex
        className={newStyles.shipmentDetailsMainPane}
        flex={isMobile ? "none" : 1}
        gap="none"
        height={isMobile ? "auto" : "full"}
        minH={isMobile ? "auto" : "none"}
        minW="none"
        overflowY={isMobile ? "visible" : "auto"}
        w={isMobile ? "full" : undefined}
      >
        <NewOutboundOrderFulfillmentGroup
          fulfillmentGroup={fulfillmentGroup}
          handleUpdates={handleUpdates}
          index={index}
          orderInfo={orderInfo}
          presets={presets}
          setRefreshSymbol={setRefreshSymbol}
          totalGroups={numGroups}
        />
      </Flex>
      <Flex
        borderColor="primary"
        borderLeftWidth={isMobile ? "none" : "1px"}
        borderStyle="solid"
        borderTopWidth={isMobile ? "1px" : "none"}
        className={newStyles.fulfillmentFormRail}
        dir="column"
        flex={isMobile ? "none" : undefined}
        height={isMobile ? "auto" : "full"}
        minH={isMobile ? "auto" : "none"}
        minW={isMobile ? "none" : undefined}
        overflow={isMobile ? "visible" : "hidden"}
        position="relative"
        shrink={isMobile ? undefined : 0}
        w={isMobile ? "full" : undefined}
      >
        <EditAddressModalProvider>
          <PackingSlipBinModalProvider>
            <PinVerificationProvider>
              <FulfillmentForm
                availableUsers={teamUsers}
                balance={balanceLoad.value?.balance}
                balanceLoading={balanceLoad.pending}
                fulfillmentGroups={[fulfillmentGroup]}
                hiddenCardIds={[
                  "items",
                  "notes",
                  "order-information",
                  "parcel-photos",
                ]}
                onChangeLocations={handleChangeLocations}
                onGroupsFulfillment={onGroupsFulfillment}
                onGroupsVoided={onGroupsVoided}
                parcelValues={parcelsLoad.value ?? []}
                permissionsToEditEveryGroup={permissionsToEditEveryGroup}
                reloadBalance={() => reloadBalance()}
                resetSelections={() => {}}
                setRows={setFulfillmentGroupData}
                setSearchRefreshSymbol={setRefreshSymbol}
                setSelectedFulfillmentGroups={
                  handleSetSelectedFulfillmentGroups
                }
                showActionCostSummary
                showHeader={false}
                showSingleParcelTrackingNumber
                triggerRefresh={triggerRefresh}
                updateRows={handleUpdates}
                updatesPending={updatesPending}
              />
            </PinVerificationProvider>
          </PackingSlipBinModalProvider>
        </EditAddressModalProvider>
      </Flex>
    </Flex>
  );
});
